import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import EmptyState from '../components/EmptyState';
import { reading } from '../domain/reading';
import ConfirmDialog from '../components/ConfirmDialog';
import DateRangePicker from '../components/DateRangePicker';
import SummaryHeader from '../components/expenses/SummaryHeader';
import SummaryPanel, { SummaryToggle } from '../components/expenses/SummaryPanel';
import ExpenseToolbar from '../components/expenses/ExpenseToolbar';
import ExpenseTable from '../components/expenses/ExpenseTable';
import AddNewDrawer from '../components/expenses/AddNewDrawer';
import EntryFormDrawer from '../components/expenses/EntryFormDrawer';
import useResourceActions from '../hooks/useResourceActions';
import NewCategoryDrawer from '../components/expenses/NewCategoryDrawer';
import AddTransactionDrawer from '../components/families/AddTransactionDrawer';
import useExpenseList from '../hooks/useExpenseList';
import useExpenseEntryForm from '../hooks/useExpenseEntryForm';
import useMileageTopUp from '../hooks/useMileageTopUp';
import { useExpenses } from '../data/useExpenses';
import { expenseCategoryErrors } from '../domain/expenses';
import { todayISO } from '../domain/clock';
import { allExpenseCategories, expenseEmptyState, truncatedWarning } from '../domain/expenseForms';
import { extractReceipt, isReceiptOcrConfigured } from '../lib/receiptOcr';
import s from './ExpensesPage.module.css';

// Expenses & Other Revenue.
//
// The screen has two independent time scopes and it is worth not collapsing
// them: the summary answers "how did this month go", while the list below
// answers "show me these transactions", and MMS lets you set them separately.
// A teacher reconciling a quarter against a single month's summary needs both.
//
// Assembly only. The rules live in useExpenseList and useExpenseEntryForm; what
// they look like lives in components/expenses.

// The studio's month, from the teacher's own calendar (`todayISO`, the day the
// agenda is drawn for), not UTC's, which on the evening of September 30 in
// California is already October (#542).
const thisMonth = () => todayISO().slice(0, 7);
const DATE_MIN = '1900-01-01';
const DATE_MAX = '2999-12-31';

export default function ExpensesPage() {
  const navigate = useNavigate();
  // The Mileage Log's expenses a recurring trip is owed (#554): the year's
  // turn, and a series saved before per-date expenses. Usually nothing.
  useMileageTopUp();
  const {
    loading, failed, summaryRead, betweenReport, summaryFor, seriesFor,
    addEntry, updateEntry, removeEntry,
    uploadAttachment, attachmentUrl, removeAttachment, attachmentsFor,
    categoriesFor, addCategory, categoryRecords,
  } = useExpenses();

  const [basis, setBasis] = useState('accrual');
  const [month, setMonth] = useState(thisMonth);
  const [showSummary, setShowSummary] = useState(true);
  const [rowMenuId, setRowMenuId] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [newCategoryError, setNewCategoryError] = useState(null);
  // The family ledger's own drawer, opened from the add panel with no family —
  // it asks for one (AddTransactionDrawer.jsx).
  const [familyTransactionOpen, setFamilyTransactionOpen] = useState(false);

  const list = useExpenseList({ betweenReport, initialMonth: thisMonth() });
  // `removeAttachment` goes in because the hook is the only place that knows
  // which receipt object a row has stopped pointing at (#94). It is not wired
  // to the Remove button, which would delete a saved entry's evidence when the
  // teacher then cancelled the form.
  //
  // `extractReceipt` goes in rather than being imported by the hook (#106):
  // receiptOcr.js reads `import.meta.env`, which no jest project can parse, and
  // this page already imports Vite-only modules.
  const entry = useExpenseEntryForm({
    addEntry, updateEntry, uploadAttachment, removeAttachment, extractReceipt, categoriesFor,
  });

  const summary = summaryFor(month, basis);
  const series = useMemo(() => seriesFor(month, basis), [seriesFor, month, basis]);
  const emptyState = expenseEmptyState(list.isFiltered);

  const formCategories = categoriesFor(entry.isRevenue ? 'revenue' : 'expense');
  // The list holds both kinds of row, so its filter offers both vocabularies.
  const allCategories = useMemo(
    () => allExpenseCategories(categoriesFor('expense'), categoriesFor('revenue')),
    [categoriesFor]
  );

  // Copy link (#515): the receipt's own thirty-day link, through the same
  // cache View uses, with the sentence after it saying how long it lasts.
  const receiptLinks = useResourceActions(attachmentUrl);

  // Fetched on demand rather than stored on the entry: every link expires
  // (#431), so a URL saved in Firestore would stop opening within hours.
  const viewAttachment = async (path) => {
    try {
      const url = await attachmentUrl(path);
      if (url) window.open(url, '_blank', 'noopener');
    } catch (err) {
      console.warn('[expenses] could not open receipt', err);
    }
  };

  // The second door into this collection, with the manager page's
  // duplicate-name refusal (#144): a teacher mid-entry who cannot find
  // "Teaching Supplies" in the select types it again here, and a second
  // document would show as one row, so the next rename would move half the
  // entries.
  const saveCategory = async () => {
    if (!newCategory.trim()) return;
    const refusals = expenseCategoryErrors(
      { id: null, type: entry.type, label: newCategory }, categoryRecords(),
    );
    if (refusals.label) { setNewCategoryError(refusals.label); return; }
    const label = await addCategory({ type: entry.type, label: newCategory });
    // Selecting it straight away is the point of adding it here rather than in
    // settings — the teacher is mid-entry.
    if (label) entry.patch({ category: label });
    setNewCategory('');
    setNewCategoryError(null);
    entry.setStep('form');
  };

  return (
    <AppShell title="Expenses & Other Revenue">
      <div className={s.page}>
        <SummaryHeader
          basis={basis}
          onBasisChange={setBasis}
          month={month}
          onMonthChange={setMonth}
        />

        {showSummary ? <SummaryPanel summary={summary} series={series} basis={basis} read={summaryRead} /> : null}
        <SummaryToggle shown={showSummary} onToggle={() => setShowSummary((v) => !v)} />

        <div className={s.rangeLine}>
          <span>Expenses and Revenue between</span>
          <DateRangePicker
            start={list.range.start}
            end={list.range.end}
            min={DATE_MIN}
            max={DATE_MAX}
            onChange={list.setRange}
          />
        </div>

        <ExpenseToolbar
          onAddNew={entry.openPicker}
          onCategories={() => navigate('/expenses/categories')}
          onImport={() => navigate('/expenses/import')}
          sort={list.sort}
          onSortBy={list.sortBy}
          onClearSort={list.clearSort}
          draft={list.draft}
          onDraftChange={list.setDraft}
          onApplyFilter={list.applyFilter}
          onClearFilter={list.clearFilter}
          categories={allCategories}
        />

        {list.truncated ? (
          <div className={s.warning}>
            {truncatedWarning(list.truncated)}
          </div>
        ) : null}

        {(!loading || failed) && list.rows.length === 0 ? (
          <div className={s.emptyWrap}>
            <EmptyState
              reading={reading(!loading, list.rows.length, failed)}
              title={emptyState.title}
              description={emptyState.description}
              actionLabel={emptyState.action}
              onAction={list.isFiltered ? list.clearFilter : entry.openPicker}
            />
          </div>
        ) : (
          <ExpenseTable
            rows={list.shown}
            attachmentsFor={attachmentsFor}
            sort={list.sort}
            onSortByDate={() => list.sortBy('occurredOn')}
            rowMenuId={rowMenuId}
            onRowMenuToggle={(key) => setRowMenuId((cur) => (cur === key ? null : key))}
            onRowMenuClose={() => setRowMenuId(null)}
            onEdit={(e) => {
              setRowMenuId(null);
              // The receipts come in beside the entry (#183): the form seeds
              // `form.attachments` from them; they are not a field on the
              // document.
              entry.openEdit(e, attachmentsFor(e.instanceOf || e.id));
            }}
            onDelete={(e) => { setRowMenuId(null); removeEntry(e.instanceOf || e.id); }}
            onOpenAttachment={viewAttachment}
            page={list.page}
            pageCount={list.pageCount}
            perPage={list.perPage}
            total={list.rows.length}
            onPageChange={list.setPage}
            onPerPageChange={list.setPerPage}
          />
        )}
      </div>

      {/* `scanEnabled` comes from the same import as `extractReceipt` above:
          this page is the only module that names the OCR client, and the
          drawer under it reads no environment of its own (#112). */}
      <AddNewDrawer
        open={entry.step === 'picker'}
        onClose={entry.closeAll}
        onPickType={entry.openForm}
        onFamilyTransaction={() => { entry.closeAll(); setFamilyTransactionOpen(true); }}
        onScan={entry.scanReceipt}
        scanning={entry.scanning}
        scanUpload={entry.scanUpload}
        error={entry.scan?.error}
        scanEnabled={isReceiptOcrConfigured}
      />

      <AddTransactionDrawer
        open={familyTransactionOpen}
        onClose={() => setFamilyTransactionOpen(false)}
      />

      <EntryFormDrawer
        open={entry.step === 'form'}
        isRevenue={entry.isRevenue}
        editing={entry.editing}
        form={entry.form}
        onChange={entry.patch}
        canSave={entry.canSave}
        errors={entry.errors}
        categories={formCategories}
        saving={entry.saving}
        scan={entry.scan}
        attaching={entry.attaching}
        attachError={entry.attachError}
        receiptUploads={entry.receiptUploads}
        onRetryReceipt={entry.retryReceipt}
        onCancelReceipt={entry.cancelReceipt}
        onDismissReceipt={entry.dismissReceipt}
        onBack={() => entry.leaveForm(() => entry.setStep('picker'))}
        onClose={() => entry.leaveForm(entry.closeAll)}
        onCancel={() => entry.leaveForm(entry.closeAll)}
        onSave={() => entry.save(false)}
        onSaveAndAdd={() => entry.save(true)}
        onAddCategory={() => { setNewCategory(''); setNewCategoryError(null); entry.setStep('category'); }}
        onAttach={entry.attach}
        onDetach={entry.detach}
        onViewAttachment={viewAttachment}
        onCopyAttachmentLink={receiptLinks.copy}
        attachmentNotice={receiptLinks.notice}
      />

      <NewCategoryDrawer
        open={entry.step === 'category'}
        value={newCategory}
        onChange={(v) => { setNewCategory(v); setNewCategoryError(null); }}
        error={newCategoryError}
        onBack={() => entry.setStep('form')}
        onSave={saveCategory}
      />

      <ConfirmDialog
        open={Boolean(entry.guard)}
        title="Unsaved Changes"
        message="Would you like to save or discard your changes before continuing?"
        // Disabled rather than inert: with no amount there is nothing to save,
        // and a Save that dismissed the dialog without writing would be worse.
        confirmDisabled={!entry.form.amount}
        onConfirm={async () => {
          const ok = await entry.save(!entry.editing);
          if (!ok) return;
          const next = entry.guard;
          entry.setGuard(null);
          next?.();
        }}
        onDiscard={() => { const next = entry.guard; entry.setGuard(null); entry.closeAll(); next?.(); }}
        onCancel={() => entry.setGuard(null)}
      />
    </AppShell>
  );
}
